/**
 * Viewer chat (docs/viewer-chat-plan.md): one bundle for the hosted player
 * (loaded only when the stream has chat on) and the hosted chat page.
 */
import './chat.css';
import { ChatClient } from './client';
import { copyFor } from './i18n';
import { mountPanel, type PanelOptions } from './panel';
import { mountReactions } from './reactions';

export interface ChatOptions extends PanelOptions {
  streamId: string;
  /** Writes: https://api.justonair.com. */
  api: string;
  /** Reads: the CDN, /api/embed on the player's host. */
  read: string;
  /** Where the panel goes. */
  host: HTMLElement;
  /** The reaction bar and the layer emoji float in; default: inside the panel. */
  reactionBar?: HTMLElement;
  floatLayer?: HTMLElement;
  /** false (?reactions=0): no reaction bar and no floating emoji, e.g. when the page draws reactions over its own video. */
  reactions?: boolean;
  lang?: string | null;
  /** ?accent= of the page, with a readable text colour on it. */
  accent?: { color: string; ink: string } | null;
}

export function startChat(o: ChatOptions) {
  const t = copyFor(o.lang ?? null);
  const client = new ChatClient(o.streamId, o.api, o.read);
  const panel = mountPanel(o.host, client, t, o);
  if (o.accent) {
    panel.root.style.setProperty('--accent', o.accent.color);
    panel.root.style.setProperty('--accent-ink', o.accent.ink);
  }
  let reactions: { destroy(): void } | null = null;
  if (o.reactions !== false) {
    let bar = o.reactionBar;
    let layer = o.floatLayer;
    if (!bar || !layer) {
      bar = document.createElement('div');
      layer = document.createElement('div');
      panel.reactSlot.append(bar);
      panel.root.append(layer);
    }
    reactions = mountReactions(bar, layer, client, t);
  }
  client.start();
  void moderation(client, panel, t);
  return {
    client,
    panel,
    copy: t,
    destroy() {
      client.stop();
      reactions?.destroy();
      panel.destroy();
    },
  };
}
export type ChatHandle = ReturnType<typeof startChat>;

/**
 * Moderator links land here as #mod=<invite>: redeemed once into this
 * browser's moderator session, then removed from the address bar. Every
 * page then asks whether that session moderates this stream.
 */
async function moderation(client: ChatClient, panel: ReturnType<typeof mountPanel>, t: ReturnType<typeof copyFor>) {
  const m = /[#&]mod=([A-Za-z0-9_-]+)/.exec(location.hash);
  if (m) {
    history.replaceState(null, '', location.pathname + location.search);
    const r = await client.redeem(m[1]);
    if (!r.ok) panel.flash(r.message || t.modLinkInvalid, 15_000);
  }
  const me = await client.moderator();
  panel.setModerator(me);
  if (me && m) panel.flash(t.modWelcome(me.name), 6000);
}
